import type { ConsumptionTableSummary } from '@protoss/shared';
import {
  Button,
  IconSize,
  ReloadIcon,
  ResizableTable,
  Tooltip,
  type ResizableColumnDef,
} from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { formatDate } from '../../../lib/datetime';
import { useConsumptionData } from '../hooks/useConsumptionData';
import { DashboardConsumptionView } from './DashboardConsumptionView';

type ConsumptionSegment = 'data' | 'dashboards';

interface ConsumptionPageProps {
  projectUuid: string;
  breadcrumb?: string;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

type ConsumptionSortKey =
  | 'tableShortName'
  | 'totalQueryCount'
  | 'totalUserDays'
  | 'latestActivityDate';

const CONSUMPTION_COLUMNS: ResizableColumnDef<ConsumptionTableSummary>[] = [
  {
    key: 'tableShortName',
    label: 'Table',
    defaultWidth: 280,
    minWidth: 120,
    maxWidth: 600,
    sortable: true,
    render: (t) => (
      <div className="consumption-table__name">
        <span className="consumption-table__short-name">{t.tableShortName}</span>
        <span className="consumption-table__full-name">{t.tableFullName}</span>
      </div>
    ),
  },
  {
    key: 'totalQueryCount',
    label: 'Queries',
    defaultWidth: 120,
    minWidth: 70,
    maxWidth: 200,
    sortable: true,
    headerClassName: 'rt-table__col--right',
    cellClassName: 'rt-table__col--right',
    render: (t) => formatNumber(t.totalQueryCount),
  },
  {
    key: 'totalUserDays',
    label: 'User-days',
    defaultWidth: 130,
    minWidth: 80,
    maxWidth: 200,
    sortable: true,
    headerClassName: 'rt-table__col--right',
    cellClassName: 'rt-table__col--right',
    render: (t) => formatNumber(t.totalUserDays),
  },
  {
    key: 'latestActivityDate',
    label: 'Last active',
    defaultWidth: 140,
    minWidth: 100,
    maxWidth: 220,
    sortable: true,
    headerClassName: 'rt-table__col--right',
    cellClassName: 'rt-table__col--right',
    render: (t) => formatDate(t.latestActivityDate),
  },
];

export function ConsumptionPage({ projectUuid, breadcrumb }: ConsumptionPageProps) {
  const [segment, setSegment] = React.useState<ConsumptionSegment>('data');

  const { settings, summary, loading, syncing, error, syncError, reload, triggerSync } =
    useConsumptionData(projectUuid);

  const [syncBanner, setSyncBanner] = React.useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const [sortKey, setSortKey] = React.useState<ConsumptionSortKey | null>(null);
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

  function handleSort(key: string) {
    const k = key as ConsumptionSortKey;
    if (k === sortKey) {
      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        // desc → reset to original order
        setSortKey(null);
      }
    } else {
      setSortKey(k);
      setSortDir('asc');
    }
  }

  async function handleSync() {
    setSyncBanner(null);
    try {
      await triggerSync();
      setSyncBanner({ type: 'success', message: 'Sync complete.' });
    } catch {
      // syncError is set on the hook; show it via banner
    }
  }

  // Show syncError as banner
  React.useEffect(() => {
    if (syncError) {
      setSyncBanner({ type: 'error', message: syncError });
    }
  }, [syncError]);

  const tables = summary?.tables ?? [];
  const totalQueries = tables.reduce(
    (sum: number, t: ConsumptionTableSummary) => sum + t.totalQueryCount,
    0
  );
  const totalUserDays = tables.reduce(
    (sum: number, t: ConsumptionTableSummary) => sum + t.totalUserDays,
    0
  );
  const sortedTables = React.useMemo(() => {
    if (!sortKey) return tables;
    return [...tables].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'tableShortName':
          cmp = a.tableShortName.localeCompare(b.tableShortName);
          break;
        case 'totalQueryCount':
          cmp = a.totalQueryCount - b.totalQueryCount;
          break;
        case 'totalUserDays':
          cmp = a.totalUserDays - b.totalUserDays;
          break;
        case 'latestActivityDate':
          cmp = (a.latestActivityDate ?? '').localeCompare(b.latestActivityDate ?? '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [tables, sortKey, sortDir]);

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (loading && segment === 'data') {
    return (
      <div className="ss-panel ss-panel--fill ss-panel--loading" aria-label="Loading Consumption">
        Loading…
      </div>
    );
  }

  // ── Error ───────────────────────────────────────────────────────────────────
  if (error && error !== 'DATABRICKS_NOT_CONFIGURED' && error !== 'CONSUMPTION_SYSTEM_TABLES_OFF') {
    return (
      <div className="ss-panel ss-panel--fill" aria-label="Consumption error">
        <PageErrorState message="Failed to load consumption data." onRetry={reload} />
      </div>
    );
  }

  // ── Not configured ──────────────────────────────────────────────────────────
  const availability = settings?.availability;
  if (!settings || availability === 'not_configured' || error === 'DATABRICKS_NOT_CONFIGURED') {
    return (
      <div className="consumption-page" aria-label="Consumption">
        <header className="consumption-page__header">
          <div className="consumption-page__header-left">
            {breadcrumb && <p className="consumption-page__breadcrumb">{breadcrumb}</p>}
            <h1 className="consumption-page__title">Consumption</h1>
          </div>
        </header>
        <div className="consumption-empty">
          <p className="consumption-empty__title">No integration configured</p>
          <p className="consumption-empty__body">
            Connect a Databricks integration and configure the Data Catalog scope to start tracking
            table-level usage.
          </p>
        </div>
      </div>
    );
  }

  // ── System tables not enabled ───────────────────────────────────────────────
  if (availability === 'system_tables_off' || error === 'CONSUMPTION_SYSTEM_TABLES_OFF') {
    return (
      <div className="consumption-page" aria-label="Consumption">
        <header className="consumption-page__header">
          <div className="consumption-page__header-left">
            {breadcrumb && <p className="consumption-page__breadcrumb">{breadcrumb}</p>}
            <h1 className="consumption-page__title">Consumption</h1>
          </div>
        </header>
        <div className="consumption-empty">
          <p className="consumption-empty__title">System tables not enabled</p>
          <p className="consumption-empty__body">
            Databricks <code>system.access.audit</code> is not accessible for this workspace. Enable
            System Tables in your Databricks workspace settings to track table usage.
          </p>
        </div>
      </div>
    );
  }

  // ── No catalog tables ───────────────────────────────────────────────────────
  if (availability === 'no_catalog_tables') {
    return (
      <div className="consumption-page" aria-label="Consumption">
        <header className="consumption-page__header">
          <div className="consumption-page__header-left">
            {breadcrumb && <p className="consumption-page__breadcrumb">{breadcrumb}</p>}
            <h1 className="consumption-page__title">Consumption</h1>
          </div>
        </header>
        <div className="consumption-empty">
          <p className="consumption-empty__title">No catalog tables configured</p>
          <p className="consumption-empty__body">
            Configure your Data Catalog scope before syncing consumption data. At least one table
            must be selected.
          </p>
        </div>
      </div>
    );
  }

  // ── Main view ───────────────────────────────────────────────────────────────
  return (
    <div className="consumption-page" aria-label="Consumption">
      {/* Header */}
      <header className="consumption-page__header">
        <div className="consumption-page__header-left">
          {breadcrumb && <p className="consumption-page__breadcrumb">{breadcrumb}</p>}
          <h1 className="consumption-page__title">Consumption</h1>
          {segment === 'data' && settings?.lastSyncedAt && (
            <p className="consumption-page__last-sync">
              Last synced {formatDate(settings.lastSyncedAt)}
            </p>
          )}
        </div>
        <div className="consumption-page__header-actions">
          {segment === 'data' && (
            <>
              <Tooltip.Simple label="Sync now">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => void handleSync()}
                  disabled={syncing}
                  aria-label="Sync consumption data from Databricks"
                >
                  {syncing && (
                    <ReloadIcon
                      width={IconSize.md}
                      height={IconSize.md}
                      aria-hidden="true"
                      className="ss-panel__action-icon--spinning"
                    />
                  )}
                  {syncing ? 'Syncing…' : 'Sync now'}
                </Button>
              </Tooltip.Simple>
              <Tooltip.Simple label="Reload">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={reload}
                  disabled={loading || syncing}
                  aria-label="Reload consumption data"
                >
                  Reload
                </Button>
              </Tooltip.Simple>
            </>
          )}
        </div>
      </header>

      {/* Segment switcher */}
      <nav className="consumption-segment" aria-label="Consumption segment">
        <button
          type="button"
          className={`consumption-segment__btn${segment === 'data' ? ' consumption-segment__btn--active' : ''}`}
          onClick={() => setSegment('data')}
          aria-pressed={segment === 'data'}
        >
          Data
        </button>
        <button
          type="button"
          className={`consumption-segment__btn${segment === 'dashboards' ? ' consumption-segment__btn--active' : ''}`}
          onClick={() => setSegment('dashboards')}
          aria-pressed={segment === 'dashboards'}
        >
          Dashboards
        </button>
      </nav>

      {/* Dashboard segment */}
      {segment === 'dashboards' && <DashboardConsumptionView projectUuid={projectUuid} />}

      {/* Data segment */}
      {segment === 'data' && (
        <>
          {/* Sync result banner */}
          {syncBanner && (
            <div
              className={`consumption-page__sync-banner consumption-page__sync-banner--${syncBanner.type}`}
              role="status"
              aria-live="polite"
            >
              {syncBanner.message}
            </div>
          )}

          {/* Summary metrics */}
          <div className="consumption-metrics" aria-label="Usage summary">
            <div className="consumption-metric">
              <span className="consumption-metric__value">{formatNumber(tables.length)}</span>
              <span className="consumption-metric__label">Tables tracked</span>
            </div>
            <div className="consumption-metric">
              <span className="consumption-metric__value">{formatNumber(totalQueries)}</span>
              <span className="consumption-metric__label">
                Total queries ({summary?.fromDate?.slice(0, 7) ?? '-'})
              </span>
            </div>
            <div className="consumption-metric">
              <span className="consumption-metric__value">{formatNumber(totalUserDays)}</span>
              <span className="consumption-metric__label">User-days</span>
            </div>
            <div className="consumption-metric">
              <span className="consumption-metric__value">{settings.storedDays}</span>
              <span className="consumption-metric__label">Days stored</span>
            </div>
          </div>

          {/* Table list */}
          <div className="consumption-table-wrap">
            <div className="consumption-table-header">
              <h2 className="consumption-table-header__title">Tables by usage</h2>
              <span className="consumption-table-header__count">
                {tables.length} table{tables.length !== 1 ? 's' : ''}
                {summary && ` · ${formatDate(summary.fromDate)} – ${formatDate(summary.toDate)}`}
              </span>
            </div>

            {sortedTables.length === 0 ? (
              <div className="consumption-empty">
                <p className="consumption-empty__title">No usage data yet</p>
                <p className="consumption-empty__body">
                  Click <strong>Sync now</strong> to pull usage data from Databricks.
                </p>
              </div>
            ) : (
              <div className="consumption-table-scroll">
                <ResizableTable
                  columns={CONSUMPTION_COLUMNS}
                  rows={sortedTables}
                  getRowKey={(t) => t.tableFullName}
                  sortKey={sortKey ?? undefined}
                  sortDir={sortDir}
                  onSort={handleSort}
                  aria-label="Consumption by table"
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
